import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { api, formatApiErr } from "@/lib/api";
import { WHATSAPP_LINK, openWhatsApp } from "@/data/site";
import { toast } from "sonner";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Message received! We'll respond within 24 hours.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiErr(err.response?.data?.detail) || "Failed to send. Try again.");
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="contact-page" className="pt-32 pb-20">
            <div className="container-vv">
                <div className="max-w-3xl">
                    <div className="label-eyebrow">Get in Touch</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mt-4 text-balance">
                        Let's <span className="italic text-gold">connect</span>.
                    </h1>
                    <p className="mt-6 text-cream-muted text-lg">
                        Questions about which service is right for you? Reach out — we typically reply within a few hours.
                    </p>
                </div>

                <div className="mt-16 grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5 space-y-4">
                        <div className="card-luxe">
                            <Mail className="h-5 w-5 text-gold mb-3" />
                            <div className="text-xs tracking-widest uppercase text-cream-muted">Email</div>
                            <a href="mailto:info@vedicbytannu.com" className="font-serif text-lg text-cream hover:text-gold" data-testid="contact-email">info@vedicbytannu.com</a>
                        </div>
                        <div className="card-luxe">
                            <Phone className="h-5 w-5 text-gold mb-3" />
                            <div className="text-xs tracking-widest uppercase text-cream-muted">WhatsApp / Phone</div>
                            <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="font-serif text-lg text-cream hover:text-gold" data-testid="contact-whatsapp">+91 98711 78773</a>
                        </div>
                        <div className="card-luxe">
                            <MapPin className="h-5 w-5 text-gold mb-3" />
                            <div className="text-xs tracking-widest uppercase text-cream-muted">Location</div>
                            <div className="font-serif text-lg text-cream">Online — Worldwide</div>
                            <div className="text-sm text-cream-muted mt-1">Based in New Delhi, India</div>
                        </div>
                        <div className="card-luxe p-0 overflow-hidden">
                            <iframe
                                title="Map"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224346.40310969787!2d76.81306636710977!3d28.647280086373263!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x52c2b7494e204dce!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1717000000000"
                                width="100%"
                                height="280"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>

                    <form onSubmit={submit} className="lg:col-span-7 card-luxe" data-testid="contact-form">
                        <h2 className="font-serif text-2xl text-cream mb-6">Send a message</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Name *</label>
                                <input className="input-luxe mt-2" required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="contact-input-name" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Email *</label>
                                <input type="email" className="input-luxe mt-2" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="contact-input-email" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Phone</label>
                                <input className="input-luxe mt-2" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contact-input-phone" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Subject</label>
                                <input className="input-luxe mt-2" value={form.subject} onChange={(e) => upd("subject", e.target.value)} data-testid="contact-input-subject" />
                            </div>
                        </div>
                        <div className="mt-4">
                            <label className="text-xs tracking-widest uppercase text-cream-muted">Message *</label>
                            <textarea rows={6} className="input-luxe mt-2" required value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="contact-input-message" />
                        </div>
                        <button type="submit" disabled={loading} className="btn-gold mt-6 disabled:opacity-50" data-testid="contact-submit">
                            {loading ? "Sending..." : <>Send Message <Send className="h-4 w-4" /></>}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
